import { findCombinedUser } from '@/helpers/findCombinedUser';
import moment from 'moment/moment';
import {
    filterAppliedLessonsResolved,
    ResolvedEnum,
} from '@/helpers/filter/appliedLessons/filterAppliedLessonsResolved';
import { CheckedEnum, filterAppliedLessonsChecked } from '@/helpers/filter/appliedLessons/filterAppliedLessonsChecked';
import type { ProjectUser } from '@/models/projectUser';
import { filterAppliedLessonsDynamicField } from '@/helpers/filter/appliedLessons/filterAppliedLessonsDynamicField';
import type { AppliedLesson } from '@/models/appliedlesson';
import { AppliedLessonLike } from '@/helpers/filter/appliedLessons/filterAppliedLessonTypes';
import type { PMAppliedLesson } from '@/models/pmappliedlesson';
import type { XAppliedLesson } from '@/models/xappliedlesson';

export const processTradeAppliedLessons = (config: {
    appliedLessons: AppliedLesson[];
    users: ProjectUser[];
    filterResolved: ResolvedEnum;
    filterChecked: CheckedEnum;
    dynamicFields: string[];
    topicIds: number[];
}) => {
    const processedLessons = processLessons(config.appliedLessons, config, (lesson) => {
        return (
            filterAppliedLessonsResolved(lesson, config.filterResolved) &&
            filterAppliedLessonsChecked(lesson, config.filterChecked) &&
            filterAppliedLessonsDynamicField(lesson, config.dynamicFields) &&
            (config.topicIds.length === 0 ||
                (lesson.lesson.topics &&
                    config.topicIds.some((topicId) => lesson.lesson.topics.some((topic) => topic.id === topicId))))
        );
    });

    return flatMapLessons(processedLessons);
};

export const processPmAppliedLessons = (config: {
    appliedLessons: PMAppliedLesson[];
    users: ProjectUser[];
    filterResolved: ResolvedEnum;
    filterChecked: CheckedEnum;
    dynamicFields: string[];
    pmSubPhasesIds: number[];
}) => {
    const processedLessons = processLessons(config.appliedLessons, config, (pmLesson) => {
        const subPhaseId = pmLesson.lesson.sub_phase ? pmLesson.lesson.sub_phase.id : 0;

        return !!(
            filterAppliedLessonsResolved(pmLesson, config.filterResolved) &&
            filterAppliedLessonsChecked(pmLesson, config.filterChecked) &&
            filterAppliedLessonsDynamicField(pmLesson, config.dynamicFields) &&
            (config.pmSubPhasesIds.length === 0 || (subPhaseId && config.pmSubPhasesIds.includes(subPhaseId)))
        );
    });

    return flatMapLessons(processedLessons);
};

export const processXAppliedLessons = (config: {
    appliedLessons: XAppliedLesson[];
    users: ProjectUser[];
    filterResolved: ResolvedEnum;
    filterChecked: CheckedEnum;
    dynamicFields: string[];
    xSubProcessesIds: number[];
}) => {
    const processedLessons = processLessons(config.appliedLessons, config, (xLesson) => {
        const spId = xLesson.lesson.x_sub_process ? xLesson.lesson.x_sub_process.id : 0;

        return !!(
            filterAppliedLessonsResolved(xLesson, config.filterResolved) &&
            filterAppliedLessonsChecked(xLesson, config.filterChecked) &&
            filterAppliedLessonsDynamicField(xLesson, config.dynamicFields) &&
            (config.xSubProcessesIds.length === 0 || (spId && config.xSubProcessesIds.includes(spId)))
        );
    });

    return flatMapLessons(processedLessons);
};

const processLessons = <T extends AppliedLessonLike>(
    lessons: T[],
    config: {
        users: ProjectUser[];
    },
    passFilter: (lesson: T) => boolean
): T[] => {
    const filteredLessons: T[] = [];
    const sortedLessons = lessons.sort((a, b) => {
        return a.lesson.order - b.lesson.order;
    });

    for (const lesson of sortedLessons) {
        lesson.responsible_user = findCombinedUser(config.users, lesson);
        if (lesson.contacttill) {
            lesson.contacttill = moment(lesson.contacttill).toDate();
        }

        const processedSubLessons = lesson.sub_lessons
            ? processLessons(lesson.sub_lessons as T[], config, passFilter)
            : [];

        const passesFilter = passFilter(lesson);

        if (passesFilter || processedSubLessons.length > 0) {
            lesson.sub_lessons = processedSubLessons as typeof lesson.sub_lessons;
            filteredLessons.push(lesson);
        }
    }

    return filteredLessons;
};

const flatMapLessons = <T extends AppliedLessonLike>(
    appliedLessons: T[],
    flattened: T[] = [],
    levels: number[] = []
) => {
    for (let index = 0; index < appliedLessons.length; index++) {
        const appliedLesson = appliedLessons[index];
        const itemLevels = [...levels, index + 1];
        appliedLesson.lesson.insight = itemLevels.join('.') + ' - ' + appliedLesson.lesson.insight;
        flattened.push(appliedLesson);
        if (appliedLesson.sub_lessons) {
            flatMapLessons(appliedLesson.sub_lessons as T[], flattened, itemLevels);
        }
    }
    return flattened;
};
